import { Spacer, VDBText } from "@components/atoms";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import { SpaceType } from "@components/atoms/Spacer/types";
import PopUp from "@components/molecules/PopUp";
import { messageType } from "@components/molecules/PopUp/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TouchableOpacity } from "react-native";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import { MfaEventNames, MfaEventTypes, sessionPopupEnum } from "../Types/types";
import { formatDate } from "../utils";
import {
  useDeleteAllTokens,
  useDeleteSingleToken,
  useGetUserLoggedInTokens,
} from "@apis/user";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import { useUserAnalytics } from "../../../../hooks/useAnalytics";

const ActiveSessionList = ({ isOpen, onSuccess, user_id, password }: any) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const [activePopup, setActivePopup] = useState<sessionPopupEnum | null>(
    sessionPopupEnum.SESSION_LIMIT_REACHED
  );
  const [selectedDevice, setSelectedDevice] = useState<any>(null);
  const [showLoader, setShowLoader] = useState<any>(null);
  const deleteSingleTokenApi = useDeleteSingleToken(user_id, password);
  const deleteAllTokensApi = useDeleteAllTokens(user_id, password);
  const [activeSessions, setActiveSessions] = useState<any>([]);

  const { trackEvent } = useUserAnalytics();
  const activeSessionList = useGetUserLoggedInTokens(user_id, password);

  useEffect(() => {
    if (activeSessionList.isFetching) return;
    if (activeSessionList.isSuccess) {
      const sortedActiveSessions =
        activeSessionList.data?.data?.data?.user_tokens &&
        activeSessionList.data?.data?.data?.user_tokens.length
          ? activeSessionList.data?.data?.data?.user_tokens.sort(
              (a: any, b: any) =>
                new Date(b.last_access_time).getTime() -
                new Date(a.last_access_time).getTime()
            )
          : [];
      setActiveSessions(sortedActiveSessions);
      if (sortedActiveSessions.length < 2) {
        onSuccess(true);
      }
    } else if (activeSessionList.isError) {
      console.error("Error fetching active sessions:", activeSessionList.error);
    }
  }, [
    activeSessionList.isSuccess,
    activeSessionList.isError,
    activeSessionList.isFetching,
  ]);

  if (!isOpen) return null;

  const getDeviceName = (deviceInfo: any) => {
    return (
      deviceInfo?.additional_info?.deviceName ??
      deviceInfo?.additional_info?.deviceType ??
      t("login.sessionLimit.popUp.deviceName.text", "Unknown Device")
    );
  };

  const getDeviceOs = (deviceInfo: any) => {
    return deviceInfo?.additional_info?.osName ||
      deviceInfo?.additional_info?.browser
      ? deviceInfo?.additional_info?.osName
        ? `${deviceInfo?.additional_info?.osName} App`
        : `${deviceInfo?.additional_info?.browser} ${deviceInfo?.additional_info?.platformType}`
      : null;
  };

  const handleLogoutButtonClick = async (deviceInfo: any) => {
    if (deviceInfo.token) {
      setSelectedDevice(deviceInfo);
      setActivePopup(sessionPopupEnum.CONFIRM_SESSION_LOGOUT);
    } else {
      console.warn("No token provided for logout.");
    }
  };

  const logoutSession = () => {
    setShowLoader(sessionPopupEnum.CONFIRM_SESSION_LOGOUT);
    deleteSingleTokenApi.mutate(
      { token: selectedDevice.token },
      {
        onSuccess: () => {
          setActivePopup(sessionPopupEnum.SESSION_LIMIT_REACHED);
          setSelectedDevice(null);
          setShowLoader(null);
          trackEvent(MfaEventNames.MFA, {
            action: MfaEventTypes.CLICK_ACTIVE_SESSIONS_ONE_SESSION_LOGGED_OUT,
            user_id,
            logged_out_device_name:
              getDeviceName(selectedDevice) || "Unknown Device",
            logged_out_device_os: getDeviceOs(selectedDevice) || "Unknown OS",
            logged_out_device_last_active:
              selectedDevice.last_access_time || "Unknown Time",
          });
          activeSessionList.refetch();
        },
        onError: (error) => {
          console.error("Logout failed:", error);
          if (
            error?.response?.data?.data?.errors[0] === "User token not found"
          ) {
            // If the token is not found, we can assume the session has already been logged out.
            activeSessionList.refetch();
            setActivePopup(sessionPopupEnum.SESSION_LIMIT_REACHED);
            setSelectedDevice(null);
            setShowLoader(null);
            showToast(
              "Error",
              t(
                "login.sessionLimit.popUp.sessionAlreadyLoggedOut.text",
                "This session has already been logged out."
              ),
              ToastTypes.ERROR,
              true,
              true
            );
          }
        },
      }
    );
  };

  const logoutAllSessions = () => {
    setShowLoader(sessionPopupEnum.CONFIRM_ALL_SESSIONS_LOGOUT);
    deleteAllTokensApi.mutate(
      {},
      {
        onSuccess: () => {
          setActivePopup(sessionPopupEnum.SESSION_LIMIT_REACHED);
          setSelectedDevice(null);
          setShowLoader(null);
          trackEvent(MfaEventNames.MFA, {
            action: MfaEventTypes.CLICK_ACTIVE_SESSIONS_ALL_SESSIONS_LOGGED_OUT,
            user_id,
          });
          activeSessionList.refetch();
        },
        onError: (error) => {
          console.error("Logout failed:", error);
          setShowLoader(null);
        },
      }
    );
  };

  const ActiveSessionItem = ({ deviceInfo, showLogout = false }: any) => {
    return (
      <ActiveDeviceContainer>
        <ActiveSessionListItem>
          <VDBText color={theme["core-05"]} style={typography["Body-M"]}>
            {getDeviceName(deviceInfo)}
          </VDBText>
          {showLogout ? (
            <TouchableOpacity
              onPress={() => handleLogoutButtonClick(deviceInfo)}
            >
              <VDBText
                color={theme["link-static"]}
                style={typography["Body-M"]}
              >
                {t("login.sessionLimit.popUp.logoutButton.text", "Logout")}
              </VDBText>
            </TouchableOpacity>
          ) : null}
        </ActiveSessionListItem>
        <Spacer size={SpaceType.ExtraSmall} />
        <ActiveSessionListItem>
          {deviceInfo?.additional_info?.osName ||
          deviceInfo?.additional_info?.browser ? (
            <VDBText color={theme["core-06"]} style={typography["Minor-R"]}>
              {getDeviceOs(deviceInfo)}
            </VDBText>
          ) : null}
        </ActiveSessionListItem>
        <Spacer size={SpaceType.ExtraSmall} />
        <ActiveSessionListItem>
          <VDBText color={theme["core-06"]} style={typography["Minor-R"]}>
            {t(
              "login.sessionLimit.popUp.lastActiveOn.text",
              "Last Active on - "
            )}
          </VDBText>
          <VDBText color={theme["core-06"]} style={typography["Minor-R"]}>
            {formatDate(deviceInfo?.last_access_time) ?? ""}
          </VDBText>
        </ActiveSessionListItem>
      </ActiveDeviceContainer>
    );
  };

  const sessionLimitReachedPopup = () => {
    return (
      <PopUp
        type={messageType.Error}
        title={t(
          "login.sessionLimit.popUp.title.text",
          "2 Active Sessions Limit Reached"
        )}
        buttons={[
          {
            type: ButtonType.primary,
            title: t(
              "login.sessionLimit.popUp.primaryButton.text",
              "Logout from All Devices"
            ),
            category: ButtonCategory.default,
            onPressAction: () =>
              setActivePopup(sessionPopupEnum.CONFIRM_ALL_SESSIONS_LOGOUT),
          },
          {
            type: ButtonType.secondary,
            title: t("login.sessionLimit.popUp.secondaryButton.text", "Cancel"),
            category: ButtonCategory.default,
            onPressAction: () => {
              setActivePopup(sessionPopupEnum.SESSION_REMAIN_ACTIVE);
            },
          },
        ]}
        hasChildren={true}
        styleKey="Body-R"
      >
        <VDBText color={theme["core-05"]} style={typography["Body-R"]}>
          {t(
            "login.sessionLimit.popUp.descriptionTitle.text",
            "Please logout of one to start a new session."
          )}
        </VDBText>
        <Spacer size={SpaceType.Double} />
        <ActiveSessions
          showsVerticalScrollIndicator={true}
          showsHorizontalScrollIndicator={true}
          keyboardShouldPersistTaps={"handled"}
          automaticallyAdjustKeyboardInsets={true}
          contentContainerStyle={{ flexGrow: 1 }}
        >
          {activeSessions && activeSessions.length
            ? activeSessions.map((session: any, index: number) => (
                <ActiveSessionItem
                  key={index}
                  deviceInfo={session}
                  showLogout={!!session.token}
                />
              ))
            : null}
        </ActiveSessions>
      </PopUp>
    );
  };

  const sessionRemainActivePopup = () => {
    return (
      <PopUp
        type={messageType.Warning}
        title={t(
          "login.sessionLimit.sessionRemainActivePopup.title.text",
          "Session Remains Active"
        )}
        buttons={[
          {
            type: ButtonType.primary,
            title: t(
              "login.sessionLimit.sessionRemainActivePopup.primaryButton.text",
              "Return to Login"
            ),
            category: ButtonCategory.default,
            onPressAction: () => {
              onSuccess(false);
            },
          },
          {
            type: ButtonType.secondary,
            title: t(
              "login.sessionLimit.sessionRemainActivePopup.secondaryButton.text",
              "Back to Active Sessions"
            ),
            category: ButtonCategory.default,
            onPressAction: () =>
              setActivePopup(sessionPopupEnum.SESSION_LIMIT_REACHED),
          },
        ]}
        HtmlSource={{
          html: t(
            "login.sessionLimit.sessionRemainActivePopup.description.text",
            "Your decision to keep both devices logged in has cancelled the logout process. This session will now close. You may return to the login screen to sign in again if needed."
          ),
        }}
        styleKey="Body-R"
      />
    );
  };

  const confrmSessionLogoutPopup = () => {
    return (
      <PopUp
        type={messageType.Warning}
        title={t(
          "login.sessionLimit.confrmSessionLogoutPopup.title.text",
          "Confirm Session Logout"
        )}
        buttons={[
          {
            type: ButtonType.primary,
            title: t(
              "login.sessionLimit.confrmSessionLogoutPopup.primaryButton.text",
              "Confirm Logout"
            ),
            category: ButtonCategory.default,
            loading: showLoader === sessionPopupEnum.CONFIRM_SESSION_LOGOUT,
            disabled: showLoader === sessionPopupEnum.CONFIRM_SESSION_LOGOUT,
            onPressAction: () => logoutSession(),
          },
          {
            type: ButtonType.secondary,
            title: t(
              "login.sessionLimit.confrmSessionLogoutPopup.secondaryButton.text",
              "Go Back"
            ),
            category: ButtonCategory.default,
            onPressAction: () =>
              setActivePopup(sessionPopupEnum.SESSION_LIMIT_REACHED),
          },
        ]}
        styleKey="Body-R"
        hasChildren={true}
      >
        <VDBText color={theme["core-05"]} style={typography["Body-R"]}>
          {t(
            "login.sessionLimit.confrmSessionLogoutPopup.descriptionTitle.text",
            "Are you sure you want to logout of this session?"
          )}
        </VDBText>
        <Spacer size={SpaceType.Double} />
        <ActiveSessionItem deviceInfo={selectedDevice} />
      </PopUp>
    );
  };

  const confirmAllSessionsLogoutPopup = () => {
    return (
      <PopUp
        type={messageType.Warning}
        title={t(
          "login.sessionLimit.confirmAllSessionsLogoutPopup.title.text",
          "Confirm Sessions Logout"
        )}
        buttons={[
          {
            type: ButtonType.primary,
            title: t(
              "login.sessionLimit.confirmAllSessionsLogoutPopup.primaryButton.text",
              "Confirm Logout"
            ),
            category: ButtonCategory.default,
            loading:
              showLoader === sessionPopupEnum.CONFIRM_ALL_SESSIONS_LOGOUT,
            disabled:
              showLoader === sessionPopupEnum.CONFIRM_ALL_SESSIONS_LOGOUT,
            onPressAction: () => logoutAllSessions(),
          },
          {
            type: ButtonType.secondary,
            title: t(
              "login.sessionLimit.confirmAllSessionsLogoutPopup.secondaryButton.text",
              "Go Back"
            ),
            category: ButtonCategory.default,
            onPressAction: () =>
              setActivePopup(sessionPopupEnum.SESSION_LIMIT_REACHED),
          },
        ]}
        HtmlSource={{
          html: t(
            "login.sessionLimit.confirmAllSessionsLogoutPopup.description.text",
            "Are you sure you want to logout of all your devices?"
          ),
        }}
        styleKey="Body-R"
      />
    );
  };

  return (
    <>
      {activePopup === sessionPopupEnum.SESSION_LIMIT_REACHED
        ? sessionLimitReachedPopup()
        : activePopup === sessionPopupEnum.SESSION_REMAIN_ACTIVE
        ? sessionRemainActivePopup()
        : activePopup === sessionPopupEnum.CONFIRM_SESSION_LOGOUT
        ? confrmSessionLogoutPopup()
        : activePopup === sessionPopupEnum.CONFIRM_ALL_SESSIONS_LOGOUT
        ? confirmAllSessionsLogoutPopup()
        : sessionLimitReachedPopup()}
    </>
  );
};

const ActiveDeviceContainer = styled.View`
  padding: ${SpaceType.Basic}px;
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: ${SpaceType.Basic}px;
  border: 1px solid ${({ theme }) => theme["core-03"]};
  margin-top: ${SpaceType.ExtraSmall}px;
  margin-bottom: ${SpaceType.ExtraSmall}px;
`;

const ActiveSessionListItem = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const ActiveSessions = styled.ScrollView`
  max-height: 200px;
  overflow: scroll;
`;

export default ActiveSessionList;
